import { Link } from 'react-router-dom';

export default function BookReadingClass() {
  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const classFlow = [
    {
      step: 1,
      icon: 'ri-book-open-line',
      title: 'Active Story Reading Time',
      description: 'Students read story chapters aloud in class with teacher guidance and support',
      color: '#153223',
      duration: 'Weekly'
    },
    {
      step: 2,
      icon: 'ri-file-list-3-line',
      title: 'Vocabulary & Comprehension',
      description: 'Learning and reviewing chapter vocabulary, discussing story elements and understanding',
      color: '#2d5a3f',
      duration: 'After Each Chapter'
    },
    {
      step: 3,
      icon: 'ri-repeat-line',
      title: 'Cycle Repeats',
      description: 'Continue reading → vocabulary → comprehension pattern throughout the book',
      color: '#3d6a4f',
      duration: 'Ongoing'
    },
    {
      step: 4,
      icon: 'ri-file-edit-line',
      title: 'Mid Book Review Test',
      description: 'Assessment to check understanding and retention of first half of the story',
      color: '#4d7a5f',
      duration: 'Halfway Point'
    },
    {
      step: 5,
      icon: 'ri-checkbox-circle-line',
      title: 'End Book Review Test',
      description: 'Comprehensive test covering entire book: vocabulary, comprehension, and story analysis',
      color: '#5d8a6f',
      duration: 'Book Completion'
    },
    {
      step: 6,
      icon: 'ri-feedback-line',
      title: 'Report & Feedback',
      description: 'Detailed progress report with strengths, areas for improvement, and next steps',
      color: '#6d9a7f',
      duration: 'After Test'
    }
  ];

  const annualEvents = [
    {
      icon: 'ri-movie-line',
      title: 'Movie Quiz Day',
      description: 'Watch book adaptations and participate in fun comprehension quizzes'
    },
    {
      icon: 'ri-trophy-line',
      title: 'Reading Achievement Awards',
      description: 'Celebrate students who complete multiple books with excellence'
    },
    {
      icon: 'ri-book-2-line',
      title: 'Story Sharing Sessions',
      description: 'Students present their favorite books and recommend to classmates'
    }
  ];

  return (
    <main className="bg-[#F5F1E8] min-h-screen">
      {/* Header */}
      <section className="bg-[#153223] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <Link 
            to="/"
            onClick={handleScrollToTop}
            className="inline-flex items-center gap-2 text-[#FFE66D] hover:text-white transition-colors mb-6 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
          <h1 className="font-['Yeseva_One',serif] text-4xl md:text-5xl mb-4">
            How Book Reading Class Works
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl">
            A structured, serious reading and comprehension program designed for steady progress
          </p>
        </div>
      </section>

      {/* Timeline Duration */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-[#FFE66D] rounded-2xl p-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <i className="ri-time-line text-4xl text-[#153223]"></i>
              <h2 className="font-['Yeseva_One',serif] text-3xl text-[#153223]">
                One Complete Book Cycle
              </h2>
            </div>
            <p className="text-xl text-[#153223] font-semibold">
              Duration: 6–16 weeks per storybook
            </p>
            <p className="text-gray-700 mt-2">
              Timeline varies based on book length, student pace, and comprehension depth
            </p>
          </div>
        </div>
      </section>

      {/* Class Flow Diagram */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-['Yeseva_One',serif] text-4xl text-[#153223] text-center mb-12">
            The Reading Cycle
          </h2>

          <div className="space-y-6">
            {classFlow.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-start gap-6">
                    <div 
                      className="w-20 h-20 flex items-center justify-center rounded-full flex-shrink-0"
                      style={{ backgroundColor: item.color }}
                    >
                      <i className={`${item.icon} text-3xl text-white`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="bg-[#FFE66D] text-[#153223] font-bold px-3 py-1 rounded-full text-sm">
                          Step {item.step}
                        </span>
                        <span className="text-gray-600 text-sm font-semibold">
                          {item.duration}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-[#153223] mb-3">
                        {item.title}
                      </h3>
                      <p className="text-gray-700 leading-relaxed text-lg">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Arrow connector */}
                {index < classFlow.length - 1 && (
                  <div className="flex justify-center py-4">
                    <i className="ri-arrow-down-line text-4xl text-[#153223]"></i>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Completion Badge */}
          <div className="mt-12 bg-gradient-to-r from-[#153223] to-[#2d5a3f] rounded-2xl p-8 text-center text-white">
            <i className="ri-medal-line text-5xl text-[#FFE66D] mb-4"></i>
            <h3 className="font-['Yeseva_One',serif] text-3xl mb-3">
              Book Completed!
            </h3>
            <p className="text-xl text-gray-200 mb-4">
              Student moves on to the next storybook in the curated reading list
            </p>
            <p className="text-gray-300">
              Each book builds upon previous vocabulary and comprehension skills
            </p>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-['Yeseva_One',serif] text-4xl text-[#153223] text-center mb-12">
            What Makes It Serious Reading
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-[#F5F1E8] rounded-xl p-6">
              <div className="w-14 h-14 flex items-center justify-center bg-[#153223] rounded-full mb-4">
                <i className="ri-focus-3-line text-2xl text-[#FFE66D]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#153223] mb-3">
                Active Reading Practice
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Students read aloud in class, building fluency, pronunciation, and confidence with teacher guidance
              </p>
            </div>

            <div className="bg-[#F5F1E8] rounded-xl p-6">
              <div className="w-14 h-14 flex items-center justify-center bg-[#153223] rounded-full mb-4">
                <i className="ri-brain-line text-2xl text-[#FFE66D]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#153223] mb-3">
                Deep Comprehension
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Not just reading words — understanding themes, characters, plot development, and story meaning
              </p>
            </div>

            <div className="bg-[#F5F1E8] rounded-xl p-6">
              <div className="w-14 h-14 flex items-center justify-center bg-[#153223] rounded-full mb-4">
                <i className="ri-book-mark-line text-2xl text-[#FFE66D]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#153223] mb-3">
                Mandatory Vocabulary Building
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Every chapter includes synonym learning and vocabulary expansion exercises for lasting language growth
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Storybooks Section */}
      <section className="py-16 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-['Yeseva_One',serif] text-4xl text-[#153223] text-center mb-4">
            The Storybooks We Read
          </h2>
          <p className="text-center text-gray-700 text-lg mb-12 max-w-3xl mx-auto">
            Students will read a curated selection of engaging titles throughout the year, carefully chosen for their rich language and meaningful themes.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#153223] rounded-lg flex-shrink-0">
                  <i className="ri-book-3-line text-2xl text-[#FFE66D]"></i>
                </div>
                <h3 className="text-lg font-bold text-[#153223]">
                  Roald Dahl Series
                </h3>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#153223] rounded-lg flex-shrink-0">
                  <i className="ri-book-3-line text-2xl text-[#FFE66D]"></i>
                </div>
                <h3 className="text-lg font-bold text-[#153223]">
                  Percy Jackson
                </h3>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#153223] rounded-lg flex-shrink-0">
                  <i className="ri-book-3-line text-2xl text-[#FFE66D]"></i>
                </div>
                <h3 className="text-lg font-bold text-[#153223]">
                  The Chronicles of Narnia
                </h3>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#153223] rounded-lg flex-shrink-0">
                  <i className="ri-book-3-line text-2xl text-[#FFE66D]"></i>
                </div>
                <h3 className="text-lg font-bold text-[#153223]">
                  Wonder
                </h3>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#153223] rounded-lg flex-shrink-0">
                  <i className="ri-book-3-line text-2xl text-[#FFE66D]"></i>
                </div>
                <h3 className="text-lg font-bold text-[#153223]">
                  The Secret Garden
                </h3>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#153223] rounded-lg flex-shrink-0">
                  <i className="ri-book-3-line text-2xl text-[#FFE66D]"></i>
                </div>
                <h3 className="text-lg font-bold text-[#153223]">
                  And More...
                </h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Annual Events */}
      <section className="py-16 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-['Yeseva_One',serif] text-4xl text-[#153223] text-center mb-4">
            Annual Class Events
          </h2>
          <p className="text-center text-gray-700 text-lg mb-12 max-w-2xl mx-auto">
            Beyond regular classes, we celebrate reading achievements and create memorable learning experiences
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {annualEvents.map((event, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 text-center"
              >
                <div className="w-20 h-20 flex items-center justify-center bg-[#FFE66D] rounded-full mx-auto mb-6">
                  <i className={`${event.icon} text-4xl text-[#153223]`}></i>
                </div>
                <h3 className="text-2xl font-bold text-[#153223] mb-3">
                  {event.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {event.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#153223] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-['Yeseva_One',serif] text-4xl mb-6">
            Ready to Start Your Child's Reading Journey?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Book a free online level test to find the perfect starting point for your child
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/level-test-registration"
              onClick={handleScrollToTop}
              className="whitespace-nowrap bg-[#FFE66D] hover:bg-[#FFD93D] text-[#153223] font-bold px-8 py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer flex items-center gap-2 text-lg"
            >
              <i className="ri-file-list-3-line text-2xl"></i>
              Register for Free Level Test
            </Link>
            <Link
              to="/"
              onClick={handleScrollToTop}
              className="whitespace-nowrap bg-white hover:bg-gray-100 text-[#153223] font-bold px-8 py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer flex items-center gap-2 text-lg"
            >
              <i className="ri-home-line text-xl"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
